import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERIES_KEYS } from "queries";
import { notificationsApi } from "./notificationsApi";

export interface NotificationsFilters {
  category?: number[];
  courseGroup?: number[];
  onlyUnread?: boolean;
}

export const useNotifications = (skip: number, limit: number, filter?: string) => {
  const queryClient = useQueryClient();
  const { isLoading, isFetching, data, refetch } = useQuery({
    queryKey: QUERIES_KEYS.NOTIFICATIONS(skip, limit, filter),
    queryFn: () => notificationsApi.fetch(skip, limit, filter),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: QUERIES_KEYS.NOTIFICATIONS(skip, limit, filter),
    });
  };

  return {
    invalidate,
    refetch,
    isLoading,
    isFetching,
    notifications: data?.data?.data ?? [],
    total: data?.data?.total ?? 0,
  };
};

export const useUnreadNotificationsCount = (category?: number[]) => {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: QUERIES_KEYS.NOTIFICATIONS_UNREAD_COUNT(category),
    queryFn: () => notificationsApi.getUnreadCount(category),
  });

  return {
    unreadCount: data?.data ?? 0,
    isLoading,
    isFetching,
  };
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => notificationsApi.markAsRead(id),
    onSuccess: () => {
      // Invalidate all notification queries
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
      });
    },
  });
};

export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (category?: number[]) => notificationsApi.markAllAsRead(category),
    onSuccess: () => {
      // Invalidate all notification queries
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
      });
    },
  });
};
